import {
  useGatewayMutation,
  useGatewayQuery,
} from "@clockwise/web-commons/src/network/apollo/gateway-provider";
import { getRenderTimeZone } from "@clockwise/web-commons/src/util/time-zone.util";
import { noop } from "lodash";
import { DateTime } from "luxon";
import React, { createContext, useCallback, useMemo } from "react";
import {
  CollaboratorsDocument,
  UpdateCustomCollaboratorsDocument,
} from "../../apollo/__generated__/Collaborators.v2.generated";
import { useSetAIError } from "./AIErrorContext";

export type Collaborators = {
  calendarId: string;
  sharedMinutes: number | null;
  isPinned: boolean;
  fullName: string | null;
  externalImageUrl: string | null;
}[];

type CollaboratorsContextValue = {
  topCollaborators: Collaborators;
  customCollaborators: Collaborators;
  loading: boolean;
  updateCollaborators: (collaborators: Collaborators) => void;
};

const emptyCollaboratorsValue = {
  topCollaborators: [],
  customCollaborators: [],
  loading: false,
  updateCollaborators: noop,
};

const CollaboratorsContext = createContext<CollaboratorsContextValue>(emptyCollaboratorsValue);

export const useCollaborators = () => {
  const context = React.useContext(CollaboratorsContext);
  if (!context) {
    throw new Error("useCollaborators must be used within a CollaboratorsContext");
  }
  return context;
};

export const CollaboratorsProvider = ({ children }: { children: React.ReactNode }) => {
  const setError = useSetAIError();
  const timeZone = getRenderTimeZone();
  const today = DateTime.now().setZone(timeZone);

  const [updateCustomCollaborators] = useGatewayMutation(UpdateCustomCollaboratorsDocument);

  const { data: colData, loading } = useGatewayQuery(CollaboratorsDocument, {
    variables: {
      start: today.minus({ weeks: 3 }).startOf("week").toISODate(),
      end: today.endOf("week").toISODate(),
      timeZone: timeZone,
    },
    onError: (error: Error) => {
      setError({
        error: error,
        showUserMessage: false,
        message: "Failed to fetch collaborators",
      });
    },
  });

  const updateCollaborators = useCallback(
    async (collaborators: Collaborators) => {
      await updateCustomCollaborators({
        variables: {
          calendarIds: collaborators.map((collab) => collab.calendarId),
        },
        optimisticResponse: {
          __typename: "Mutation",
          updateCustomCollaborators: {
            __typename: "User",
            id: colData?.currentUser?.id || "",
            customCollaborators: collaborators.map((collab) => {
              return {
                __typename: "Collaborator",
                id: collab.calendarId,
                fullName: collab.fullName,
                externalImageUrl: collab.externalImageUrl,
              };
            }),
          },
        },
      });
    },
    [colData?.currentUser?.id, updateCustomCollaborators],
  );

  const customCollaborators = useMemo(
    () =>
      colData?.currentUser?.customCollaborators?.map((customCollaborator) => {
        const topCollaboratorMatch = colData?.currentUser?.topCollaborators?.find((topCollab) => {
          return topCollab.id === customCollaborator.id;
        });
        return {
          calendarId: customCollaborator.id,
          sharedMinutes: topCollaboratorMatch?.sharedMinutes ?? 0,
          isPinned: true,
          fullName: customCollaborator.fullName,
          externalImageUrl: customCollaborator.externalImageUrl,
        };
      }) ?? [],
    [colData?.currentUser],
  );

  const topCollaborators = useMemo(
    () =>
      colData?.currentUser?.topCollaborators?.map((topCollaborator) => ({
        calendarId: topCollaborator.id,
        sharedMinutes: topCollaborator.sharedMinutes,
        isPinned: false,
        fullName: topCollaborator.fullName,
        externalImageUrl: topCollaborator.externalImageUrl,
      })) ?? [],
    [colData?.currentUser],
  );

  const value = useMemo(
    () => ({ topCollaborators, customCollaborators, loading, updateCollaborators }),
    [topCollaborators, customCollaborators, updateCollaborators, loading],
  );

  return <CollaboratorsContext.Provider value={value}>{children}</CollaboratorsContext.Provider>;
};
